// @ts-nocheck

"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle } from "lucide-react"
import { authAPI } from "@/lib/api"
import { useRouter, useSearchParams } from "next/navigation"

interface FormErrors {
    email?: string
    password?: string
    general?: string
}

interface FormData {
    email: string
    password: string
    rememberMe: boolean
}

export function LoginForm() {
    const router = useRouter()
    const searchParams = useSearchParams()
    const redirectTo = searchParams.get('redirect') || '/admin'

    const [showPassword, setShowPassword] = useState(false)
    const [formData, setFormData] = useState<FormData>({
        email: "",
        password: "",
        rememberMe: false
    })
    const [isLoading, setIsLoading] = useState(false)
    const [errors, setErrors] = useState<FormErrors>({})
    const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({
        email: false,
        password: false
    })

    // Load saved email if remember me was used before
    useEffect(() => {
        const savedEmail = localStorage.getItem('rememberedEmail')
        if (savedEmail) {
            setFormData(prev => ({ ...prev, email: savedEmail, rememberMe: true }))
        }
    }, [])

    // Email validation function
    const validateEmail = (email: string): string | null => {
        if (!email.trim()) {
            return "Email is required"
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return "Please enter a valid email address"
        }
        return null
    }

    // Password validation function
    const validatePassword = (password: string): string | null => {
        if (!password) {
            return "Password is required"
        }
        if (password.length < 6) {
            return "Password must be at least 6 characters long"
        }
        return null
    }

    // Real-time validation
    const validateField = (name: keyof FormData, value: string | boolean) => {
        let error = null

        switch (name) {
            case 'email':
                error = validateEmail(value as string)
                break
            case 'password':
                error = validatePassword(value as string)
                break
        }

        setErrors(prev => ({ ...prev, [name]: error || undefined }))
        return error === null
    }

    // Handle input changes with validation
    const handleInputChange = (name: keyof FormData, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [name]: value }))

        // Clear general error when user starts typing
        if (errors.general) {
            setErrors(prev => ({ ...prev, general: undefined }))
        }

        // Validate on change if field was touched
        if (touched[name as keyof typeof touched]) {
            validateField(name, value)
        }
    }

    // Handle blur events for validation
    const handleBlur = (name: keyof FormData) => {
        setTouched(prev => ({ ...prev, [name]: true }))
        validateField(name, formData[name])
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Mark all fields as touched
        setTouched({ email: true, password: true })

        // Validate all fields
        const emailError = validateEmail(formData.email)
        const passwordError = validatePassword(formData.password)

        if (emailError || passwordError) {
            setErrors({
                email: emailError || undefined,
                password: passwordError || undefined
            })
            return
        }

        setIsLoading(true)
        setErrors({})

        try {
            const response = await authAPI.signIn({
                login: formData.email.trim(),
                password: formData.password
            })

            if (response.success) {
                console.log("Login successful:", response.message)

                // Handle remember me functionality
                if (formData.rememberMe) {
                    localStorage.setItem('rememberedEmail', formData.email.trim())
                } else {
                    localStorage.removeItem('rememberedEmail')
                }

                authAPI.setUserDetails(response.user)

                // Redirect to intended destination or admin dashboard
                router.push(redirectTo)
            } else {
                // Handle specific error types
                let errorMessage = response.message || "Login failed"

                // Check for common error patterns
                if (errorMessage.toLowerCase().includes('invalid') ||
                    errorMessage.toLowerCase().includes('wrong')) {
                    errorMessage = "Invalid email or password. Please check your credentials."
                } else if (errorMessage.toLowerCase().includes('network') ||
                          errorMessage.toLowerCase().includes('connection')) {
                    errorMessage = "Network error. Please check your internet connection and try again."
                }

                setErrors({ general: errorMessage })
            }
        } catch (error: any) {
            console.error("Login error:", error)

            let errorMessage = "An unexpected error occurred. Please try again."

            // Handle different types of network errors
            if (!navigator.onLine) {
                errorMessage = "You appear to be offline. Please check your internet connection."
            } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('network')) {
                errorMessage = "Unable to connect to the server. Please try again later."
            } else if (error.response?.status === 429) {
                errorMessage = "Too many login attempts. Please wait a few minutes before trying again."
            } else if (error.response?.status >= 500) {
                errorMessage = "Server error. Please try again later."
            }

            setErrors({ general: errorMessage })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Card className="border-border shadow-lg">
            <CardHeader className="space-y-1 pb-4">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-card-foreground">Sign In</h2>
                    <p className="text-sm text-muted-foreground mt-1">Enter your credentials to continue</p>
                </div>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium text-foreground">
                            Email Address
                        </Label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                id="email"
                                type="email"
                                placeholder="Enter your email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                onBlur={() => handleBlur('email')}
                                className={`pl-10 bg-input border-border focus:ring-ring ${
                                    errors.email ? 'border-red-500 focus:ring-red-500' : ''
                                }`}
                                required
                                aria-invalid={!!errors.email}
                                aria-describedby={errors.email ? "email-error" : undefined}
                                disabled={isLoading}
                            />
                            {touched.email && !errors.email && formData.email && (
                                <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500 w-4 h-4" />
                            )}
                        </div>
                        {errors.email && (
                            <div id="email-error" className="flex items-center space-x-1 text-sm text-red-600" role="alert">
                                <AlertCircle className="w-4 h-4" />
                                <span>{errors.email}</span>
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-sm font-medium text-foreground">
                            Password
                        </Label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter your password"
                                value={formData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                onBlur={() => handleBlur('password')}
                                className={`pl-10 pr-10 bg-input border-border focus:ring-ring ${
                                    errors.password ? 'border-red-500 focus:ring-red-500' : ''
                                }`}
                                required
                                aria-invalid={!!errors.password}
                                aria-describedby={errors.password ? "password-error" : undefined}
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                disabled={isLoading}
                            >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        {errors.password && (
                            <div id="password-error" className="flex items-center space-x-1 text-sm text-red-600" role="alert">
                                <AlertCircle className="w-4 h-4" />
                                <span>{errors.password}</span>
                            </div>
                        )}
                    </div>

                    {errors.general && (
                        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md" role="alert">
                            <div className="flex items-center space-x-1">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <span>{errors.general}</span>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <input
                                id="remember"
                                type="checkbox"
                                checked={formData.rememberMe}
                                onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-ring disabled:opacity-50"
                                disabled={isLoading}
                            />
                            <Label htmlFor="remember" className="text-sm text-muted-foreground">
                                Remember me
                            </Label>
                        </div>
                        <a
                            href="/forgot-password"
                            className="text-sm text-accent hover:text-accent/80 font-medium disabled:opacity-50"
                            tabIndex={isLoading ? -1 : 0}
                        >
                            Forgot password?
                        </a>
                    </div>

                    <Button
                        type="submit"
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium disabled:opacity-50"
                        disabled={isLoading || !formData.email || !formData.password}
                    >
                        {isLoading ? (
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                                <span>Signing in...</span>
                            </div>
                        ) : (
                            "Sign In"
                        )}
                    </Button>
                </form>
            </CardContent>
        </Card>
    )
}
